import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft, ChevronRight, Plus, Trash2, Settings, CalendarDays,
  ArrowDownLeft, ArrowUpRight, Check, Pencil, X, RefreshCw, AlertTriangle,
} from 'lucide-react';
import { useWS } from '../WSContext';

// Kişisel bütçe — klasik defter. Kurgu backend/python/budget_db.py başında.
//
// İki ilke arayüze de yansıyor:
//   • DURUM TÜRETİLİR: hiçbir yerde "ödendi mi" kutusu yok, yalnız "x tutar ödendi"
//     satırları var. Kalan bunlardan çıkıyor → tutarla çelişemiyor.
//   • KUR YOK: her para birimi kendi hattında toplanıyor, tek bir "toplam" basmıyoruz.
//     Basmak uydurma bir kura bağlı olurdu.

interface Hareket {
  id: number; tarih: string; tutar: number; kisiId: number; notu: string;
  kur: number; kurHam: number; usd: number | null;
}
interface Kalem {
  id: number; ay: string; sablonId: number; kisiId: number; ad: string;
  yon: 'gelir' | 'gider'; tur: string; para: string; tutar: number; vade: string; notu: string;
  odenen: number; kalan: number; durum: 'bekliyor' | 'kismi' | 'odendi'; hareketler: Hareket[];
  // Kredi kartı asgari ödemesi. 0 = asgarisi yok. Hepsi TÜRETİLMİŞ, saklanan
  // tek şey asgari tutarın kendisi.
  asgari: number; asgariOdendi: boolean; asgariKalan: number;
  // Yalnız TRY kalemlerinde dolu (backend FX_PARA):
  usdTutar?: number | null; usdKalan?: number | null; usdOdenen?: number; usdKurYok?: boolean;
  usdAsgariKalan?: number | null;
}
// TRY -> USDC kuru. bid = Binance TR alis, efektif = komisyon dusulmus hali.
interface Fx { para: string; bid: number; kom: number; efektif: number; kaynak: string; ts: number }
interface Kisi { id: number; ad: string; emoji: string; sira: number }
interface Sablon {
  id: number; kisi_id: number; ad: string; yon: string; tur: string; para: string;
  gun: number; tutar: number; otomatik: number; bas_ay: string; son_ay: string;
  aktif: number; notu: string; vade?: string;
}
interface Ozet {
  para: string; gelirBeklenen: number; gelirGelen: number; gelirKalan: number;
  giderBeklenen: number; giderOdenen: number; giderKalan: number;
  net: number; netGerceklesen: number;
  usdGiderOdenen?: number; usdGiderKalan?: number;
}
interface Ay {
  success: boolean; error?: string; ay: string; kalemler: Kalem[]; bekleyen: Sablon[];
  ozet: Ozet[]; kisiler: Kisi[]; sablonlar: Sablon[]; fx: Fx;
}

const PARALAR = ['EUR', 'TRY', 'USD'] as const;
const SIMGE: Record<string, string> = { EUR: '€', TRY: '₺', USD: '$' };
const TURLER = ['kart', 'kira', 'kredi', 'abonelik', 'maas', 'diger'] as const;
const TUR_AD: Record<string, string> = {
  kart: 'kredi kartı', kira: 'kira', kredi: 'kredi',
  abonelik: 'abonelik', maas: 'maaş', diger: 'diğer',
};
const AY_AD = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const buAy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const ayKaydir = (ay: string, delta: number) => {
  const y = parseInt(ay.slice(0, 4), 10), m = parseInt(ay.slice(5, 7), 10) - 1 + delta;
  const d = new Date(y, m, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const ayEtiket = (ay: string) => `${AY_AD[parseInt(ay.slice(5, 7), 10) - 1]} ${ay.slice(0, 4)}`;
const para = (n: number, p: string) =>
  `${SIMGE[p] || ''}${(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const gunu = (vade: string) => (vade ? parseInt(vade.slice(8, 10), 10) : 0);
// Yaklasik USDC karsiligi. null = kur yok -> sayi uydurmuyoruz.
const usdc = (n: number | null | undefined) =>
  n == null ? '' : '≈$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const kurZaman = (ts: number) => {
  if (!ts) return '';
  const dk = Math.floor((Date.now() - ts) / 60000);
  if (dk < 1) return 'simdi';
  if (dk < 60) return dk + ' dk once';
  const sa = Math.floor(dk / 60);
  return sa < 24 ? sa + ' sa once' : Math.floor(sa / 24) + ' gun once';
};
const bugun = () => new Date().toISOString().slice(0, 10);

interface Props { dark?: boolean }

export default function Budget({ dark = true }: Props) {
  const { sendWs, subscribe, connected } = useWS() as any;

  const [ay, setAy]       = useState(buAy());
  const [d, setD]         = useState<Ay | null>(null);
  const [err, setErr]     = useState('');
  const [ayar, setAyar]   = useState(false);
  const [acik, setAcik]   = useState<Set<number>>(new Set());
  const [yeni, setYeni]   = useState(false);

  const t = {
    bg:    dark ? 'bg-[#030b1a]' : 'bg-[#f5f8fc]',
    text:  dark ? 'text-blue-50' : 'text-slate-800',
    card:  dark ? 'bg-[#0a1628] border-[#164a9e]' : 'bg-white border-[#d4e0f0]',
    sub:   dark ? 'bg-[#061530] border-[#0a2560]' : 'bg-[#eef3fa] border-[#d4e0f0]',
    muted: dark ? 'text-blue-300/70' : 'text-slate-500',
    inp:   dark ? 'bg-[#03112b] border-[#0a2560] text-blue-50'
                : 'bg-white border-[#d4e0f0] text-slate-800',
  };
  const alan = `px-2 py-1.5 rounded-lg border text-xs ${t.inp}`;

  const yukle = useCallback((a: string) => sendWs?.({ type: 'budget_month', payload: { ay: a } }), [sendWs]);

  useEffect(() => {
    const unsub = subscribe?.((m: any) => {
      if (m.type === 'budget_month') {
        if (m.payload?.success === false) setErr(m.payload?.error || 'ay okunamadı');
        else { setErr(''); setD(m.payload); }
      } else if (m.type === 'budget_saved' && m.payload?.success === false) {
        setErr(m.payload?.error || 'kaydedilemedi');
      }
    });
    return () => unsub?.();
  }, [subscribe]);

  useEffect(() => { if (connected) yukle(ay); }, [connected, ay, yukle]);

  const kisi = useCallback((id: number) => d?.kisiler.find(k => k.id === id), [d]);
  const gonder = (type: string, payload: any) => { setErr(''); sendWs?.({ type, payload: { ...payload, ay } }); };

  // Vade gününe göre grupla — her kolon gün gün akan kendi listesi.
  // GİDER ve GELİR ayrı kolonlarda: aynı gün içinde karışınca "bu ay ne ödeyeceğim"
  // sorusu, araya giren maaş satırlarıyla bölünüyordu. Gruplama yönden SONRA
  // yapılıyor, yoksa bir kolonda kalem olmayan günler boş başlık basardı.
  const gunGrupla = (ks: Kalem[]) => {
    const g: Record<number, Kalem[]> = {};
    for (const k of ks) (g[gunu(k.vade)] ||= []).push(k);
    return Object.entries(g).map(([gn, l]) => ({ gun: Number(gn), kalemler: l }))
      .sort((a, b) => a.gun - b.gun);
  };
  const kolonlar = useMemo(() => {
    const ks = d?.kalemler || [];
    return {
      gider: gunGrupla(ks.filter(k => k.yon !== 'gelir')),
      gelir: gunGrupla(ks.filter(k => k.yon === 'gelir')),
    };
  }, [d]);
  const kalemSayisi = d?.kalemler.length || 0;

  const buAyMi = ay === buAy();
  const bugunGun = new Date().getDate();

  return (
    <div className={`min-h-screen ${t.bg} ${t.text} p-4 md:p-6`}>
      <div className="max-w-6xl mx-auto">
        {/* başlık + ay seçici */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <button onClick={() => window.location.assign('/')}
            className={`text-xs px-2.5 py-1 rounded-lg border ${t.sub} ${t.muted} hover:text-sky-400`}>
            ← panel
          </button>
          {/* LP Trax ayrı kök: sekme değil, tam sayfa yönlendirme. */}
          <button onClick={() => window.location.assign('/lptrax')}
            title="LP Trax — pozisyon bazlı LP takibi"
            className={`text-xs px-2.5 py-1 rounded-lg border ${t.sub} ${t.muted} hover:text-sky-400`}>
            lp trax
          </button>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <CalendarDays size={17} className="text-sky-400" /> Bütçe
          </h1>
          <div className="flex items-center gap-1 ml-2">
            <button onClick={() => setAy(a => ayKaydir(a, -1))}
              className={`p-1 rounded-lg border ${t.sub} ${t.muted} hover:text-sky-400`}><ChevronLeft size={14} /></button>
            <span className="text-sm font-medium tabular-nums w-28 text-center">{ayEtiket(ay)}</span>
            <button onClick={() => setAy(a => ayKaydir(a, 1))}
              className={`p-1 rounded-lg border ${t.sub} ${t.muted} hover:text-sky-400`}><ChevronRight size={14} /></button>
            {!buAyMi && (
              <button onClick={() => setAy(buAy())}
                className="text-[11px] px-2 py-1 rounded-lg border border-sky-500/40 text-sky-400 hover:bg-sky-500/10">
                bu ay
              </button>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setYeni(v => !v)}
              className="text-[11px] px-2.5 py-1 rounded-lg border border-sky-500/45 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20">
              <Plus size={11} className="inline -mt-0.5" /> tek seferlik
            </button>
            <button onClick={() => setAyar(v => !v)}
              className={`text-[11px] px-2.5 py-1 rounded-lg border ${ayar
                ? 'border-sky-500/50 bg-sky-500/15 text-sky-300' : `${t.sub} ${t.muted} hover:text-sky-400`}`}>
              <Settings size={11} className="inline -mt-0.5" /> kişi & şablon
            </button>
          </div>
        </div>

        {err && (
          <div className="mb-3 px-3 py-2 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-300 text-xs flex items-center gap-2">
            {err}<button onClick={() => setErr('')} className="ml-auto"><X size={13} /></button>
          </div>
        )}
        {!connected && <p className={`text-xs ${t.muted} mb-3`}>Bağlantı bekleniyor…</p>}

        {d?.fx && <KurSeridi fx={d.fx} t={t} alan={alan} gonder={gonder} />}

        {/* para birimi başına özet — her kur kendi hattında */}
        {!!d?.ozet.length && (
          <div className="grid gap-2.5 mb-4" style={{ gridTemplateColumns: `repeat(${Math.min(d.ozet.length, 3)}, minmax(0,1fr))` }}>
            {d.ozet.map(o => (
              <div key={o.para} className={`rounded-xl border ${t.card} p-3`}>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-semibold text-sm">{o.para}</span>
                  <span className={`text-[11px] ${t.muted}`}>net</span>
                  <span className={`ml-auto text-base font-semibold tabular-nums ${
                    o.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {o.net >= 0 ? '+' : '−'}{para(Math.abs(o.net), o.para)}
                  </span>
                </div>
                <div className="space-y-1 text-[11px] tabular-nums">
                  <div className="flex items-center gap-1.5">
                    <ArrowDownLeft size={11} className="text-emerald-400 shrink-0" />
                    <span className={t.muted}>gelir</span>
                    <span className="ml-auto">
                      <span className="text-emerald-400">{para(o.gelirGelen, o.para)}</span>
                      <span className={t.muted}> / {para(o.gelirBeklenen, o.para)}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <ArrowUpRight size={11} className="text-rose-400 shrink-0" />
                    <span className={t.muted}>gider</span>
                    <span className="ml-auto">
                      <span className="text-rose-400">{para(o.giderOdenen, o.para)}</span>
                      <span className={t.muted}> / {para(o.giderBeklenen, o.para)}</span>
                    </span>
                  </div>
                  <div className={`flex items-center gap-1.5 pt-1 border-t ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'}`}>
                    <span className={t.muted}>ödenecek kalan</span>
                    <span className={`ml-auto font-medium ${o.giderKalan > 0 ? 'text-amber-400' : t.muted}`}>
                      {para(o.giderKalan, o.para)}
                    </span>
                  </div>
                  {/* Yalnız TRY: ödenen kısım HER ÖDEMENİN kendi kuruyla, kalan
                      güncel kurla. İki sayı aynı birimde ama farklı nitelikte —
                      biri gerçekleşmiş maliyet, biri tahmin; bu yüzden ayrı. */}
                  {o.usdGiderKalan != null && (
                    <div className={`flex items-center gap-1.5 ${t.muted}`}>
                      <span>USDC karşılığı</span>
                      <span className="ml-auto">
                        <span className="text-sky-300" title="ödenen — her ödemenin kendi kuruyla">
                          {usdc(o.usdGiderOdenen)}
                        </span>
                        {(o.usdGiderKalan || 0) > 0 && (
                          <span title="kalan — güncel kurla tahmin"> + {usdc(o.usdGiderKalan)}</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ekstresi girilmemiş kartlar */}
        {!!d?.bekleyen.length && <BekleyenKartlar d={d} t={t} alan={alan} gonder={gonder} />}

        {yeni && <TekSeferlik d={d} t={t} alan={alan} ay={ay}
          kapat={() => setYeni(false)} gonder={gonder} />}

        {ayar && <Ayarlar d={d} t={t} alan={alan} gonder={gonder} />}

        {/* gün gün akan liste — solda gider, sağda gelir */}
        {d && kalemSayisi === 0 && (
          <p className={`text-sm ${t.muted} py-10 text-center`}>
            Bu ayda kalem yok. Şablon tanımla (kira, kredi, maaş) ya da tek seferlik bir kalem ekle.
          </p>
        )}
        {kalemSayisi > 0 && (
          <div className="grid gap-x-4 gap-y-2 lg:grid-cols-2 items-start">
            {([['gider', 'Giderler'], ['gelir', 'Gelirler']] as const).map(([yon, baslik]) => (
              <section key={yon}>
                <div className={`flex items-center gap-1.5 px-1 pb-1.5 mb-0.5 border-b ${
                  dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'}`}>
                  {yon === 'gider'
                    ? <ArrowUpRight size={13} className="text-rose-400" />
                    : <ArrowDownLeft size={13} className="text-emerald-400" />}
                  <span className="text-xs font-semibold">{baslik}</span>
                  <span className={`text-[10px] ${t.muted}`}>
                    {kolonlar[yon].reduce((n, g) => n + g.kalemler.length, 0)} kalem
                  </span>
                </div>
                {kolonlar[yon].length === 0 && (
                  <p className={`text-[11px] ${t.muted} py-4 px-1`}>
                    {yon === 'gider' ? 'Bu ayda gider kalemi yok.' : 'Bu ayda gelir kalemi yok.'}
                  </p>
                )}
                <div className="space-y-1.5">
                  {kolonlar[yon].map(({ gun, kalemler }) => (
                    <div key={gun}>
                      <div className={`flex items-center gap-2 px-1 mb-1 mt-3 text-[11px] ${t.muted}`}>
                        <span className={`font-semibold tabular-nums ${buAyMi && gun === bugunGun ? 'text-sky-400' : ''}`}>
                          {String(gun).padStart(2, '0')}
                        </span>
                        <span className={`flex-1 h-px ${dark ? 'bg-[#0a2560]' : 'bg-[#d4e0f0]'}`} />
                        {buAyMi && gun < bugunGun && <span className="text-[10px] opacity-60">geçti</span>}
                      </div>
                      {kalemler.map(k => (
                        <KalemSatiri key={k.id} k={k} t={t} alan={alan} dark={dark} kisi={kisi}
                          kisiler={d?.kisiler || []} gonder={gonder} fx={d?.fx}
                          acik={acik.has(k.id)}
                          topla={() => setAcik(s => {
                            const n = new Set(s); n.has(k.id) ? n.delete(k.id) : n.add(k.id); return n;
                          })} />
                      ))}
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── kur şeridi (TRY → USDC) ──────────────────────────────────────────────────
// Neden burada: TRY ödemeleri kriptodan gelen USDC'nin Binance TR'de satılmasıyla
// yapılıyor, satırın yanındaki "≈$" bu kurdan geliyor. Kur ÇEKİLİNCE yalnız
// ÖDENMEMİŞ kalanların tahmini değişir; ödenmiş satırlar kendi kurunu saklıyor.
//
// Çekme ucu: binance.tr open/v1 market/depth (API key istemiyor). Otomatik
// döngü YOK — kullanıcı kararı: ne zaman satış yapacaksa o zaman çekiyor.
function KurSeridi({ fx, t, alan, gonder }: any) {
  const [bid, setBid] = useState('');
  const [kom, setKom] = useState('');
  const [bekle, setBekle] = useState(false);

  // Sunucudan yeni kur gelince düzenleme alanları boşaltılır: aksi halde elle
  // yazılmış eski değer ekranda kalıp çekilen kuru gizliyordu.
  useEffect(() => { setBid(''); setKom(''); setBekle(false); }, [fx.bid, fx.kom, fx.ts]);

  const yaz = () => {
    const b = bid.trim() ? parseFloat(bid.replace(',', '.')) : null;
    const k = kom.trim() ? parseFloat(kom.replace(',', '.')) : null;
    if (b == null && k == null) return;
    gonder('budget_save_fx', { bid: b, kom: k });
  };
  const cek = () => { setBekle(true); gonder('budget_fx_fetch', {}); };

  const degisik = !!bid.trim() || !!kom.trim();
  return (
    <div className={`rounded-xl border ${t.card} px-3 py-2 mb-4 flex items-center gap-2 flex-wrap text-xs`}>
      <span className="font-semibold">USDC/{fx.para}</span>
      {fx.bid > 0 ? (
        <>
          <span className="tabular-nums" title="Binance TR alış (bid)">{fx.bid}</span>
          <span className={t.muted}>− %{fx.kom} →</span>
          <span className="tabular-nums text-sky-300 font-medium" title="komisyon düşülmüş efektif kur">
            {fx.efektif}
          </span>
          <span className={`text-[10px] ${t.muted}`}>
            {fx.kaynak === 'elle' ? 'elle girildi' : fx.kaynak} · {kurZaman(fx.ts)}
          </span>
        </>
      ) : (
        <span className="text-amber-400 text-[11px]">
          kur yok — TRY satırlarının USDC karşılığı gösterilemiyor
        </span>
      )}
      <div className="ml-auto flex items-center gap-1.5">
        <input value={bid} onChange={e => setBid(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') yaz(); }}
          placeholder="bid elle" inputMode="decimal"
          className={`${alan} w-24 tabular-nums`} />
        <input value={kom} onChange={e => setKom(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') yaz(); }}
          placeholder={`kom %${fx.kom}`} inputMode="decimal"
          className={`${alan} w-20 tabular-nums`} />
        {degisik && (
          <button onClick={yaz}
            className="px-2 py-1.5 rounded-lg text-[11px] border border-sky-500/45 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20">
            kaydet
          </button>
        )}
        <button onClick={cek} disabled={bekle}
          title="Binance TR emir defterinden güncel alış kurunu çek"
          className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] border ${t.sub} ${t.muted} hover:text-sky-400 disabled:opacity-50`}>
          <RefreshCw size={11} className={bekle ? 'animate-spin' : ''} /> kuru çek
        </button>
      </div>
    </div>
  );
}

// ── ekstresi bekleyen kartlar ────────────────────────────────────────────────
// Kart şablonu ad/para/günü taşıyor, tutarı her ay elle giriliyor. Böylece kart
// her ay yeniden tanımlanmıyor; yalnız ekstre tutarı yazılıyor.
function BekleyenKartlar({ d, t, alan, gonder }: any) {
  const [tutar, setTutar] = useState<Record<number, string>>({});
  const [asg, setAsg]     = useState<Record<number, string>>({});
  const gir = (s: Sablon) => {
    const v = parseFloat((tutar[s.id] || '').replace(',', '.'));
    if (!(v > 0)) return;
    gonder('budget_save_kalem', {
      sablonId: s.id, kisiId: s.kisi_id, ad: s.ad, yon: s.yon, tur: s.tur,
      para: s.para, tutar: v, vade: s.vade,
      asgari: parseFloat((asg[s.id] || '').replace(',', '.')) || 0,
    });
    setTutar(p => ({ ...p, [s.id]: '' }));
    setAsg(p => ({ ...p, [s.id]: '' }));
  };
  return (
    <div className={`rounded-xl border ${t.card} p-3 mb-4`}>
      <div className={`text-[11px] ${t.muted} mb-2`}>
        Ekstresi girilmemiş ({d.bekleyen.length}) — tutarı yaz, ay listesine düşsün.
        Asgari boş bırakılabilir, sonradan da girilebilir.
      </div>
      <div className="space-y-1.5">
        {d.bekleyen.map((s: Sablon) => (
          <div key={s.id} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg border ${t.sub}`}>
            <span className={`tabular-nums w-6 ${t.muted}`}>{String(s.gun).padStart(2, '0')}</span>
            <span className="font-medium">{s.ad}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${t.muted} border ${t.sub}`}>{s.para}</span>
            <input value={tutar[s.id] || ''} onChange={e => setTutar(p => ({ ...p, [s.id]: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') gir(s); }}
              placeholder="ekstre tutarı" inputMode="decimal"
              className={`${alan} ml-auto w-32 tabular-nums`} />
            <input value={asg[s.id] || ''} onChange={e => setAsg(p => ({ ...p, [s.id]: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') gir(s); }}
              placeholder="asgari" inputMode="decimal" title="asgari ödeme tutarı (zorunlu değil)"
              className={`${alan} w-24 tabular-nums`} />
            <button onClick={() => gir(s)}
              className="px-2 py-1 rounded-lg text-[11px] border border-sky-500/45 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20">
              ekle
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── tek bir kalem satırı + parça ödemeleri ───────────────────────────────────
function KalemSatiri({ k, t, alan, dark, kisi, kisiler, gonder, acik, topla, fx }: any) {
  const [tutar, setTutar] = useState('');
  const [tarih, setTarih] = useState(bugun());
  const [kid, setKid]     = useState<number>(k.kisiId || kisiler[0]?.id || 0);
  // Bu ödemenin kuru. Ayardaki güncel bid ile açılıyor; o sabah başka fiyattan
  // satıldıysa elle değiştirilebiliyor ve SATIRA o yazılıyor (geri dönük
  // düzeltme yok — ödeme kaydedildikten sonra kuru kendi satırında donuyor).
  const fxli = !!fx && k.para === fx.para;
  const [bid, setBid] = useState('');
  useEffect(() => { if (fxli) setBid(fx.bid ? String(fx.bid) : ''); }, [fxli, fx?.bid]);

  const gelir = k.yon === 'gelir';
  const renk = k.durum === 'odendi' ? 'text-emerald-400'
    : k.durum === 'kismi' ? 'text-amber-400' : t.muted;
  const sahip = kisi(k.kisiId);

  const kurAl = () => (fxli ? parseFloat((bid || '').replace(',', '.')) || 0 : 0);
  const ode = () => {
    const v = parseFloat(tutar.replace(',', '.'));
    if (!(v > 0)) return;
    gonder('budget_save_hareket', { kalemId: k.id, tutar: v, tarih, kisiId: kid, kur: kurAl() });
    setTutar('');
  };
  const tamami = () => {
    if (!(k.kalan > 0)) return;
    gonder('budget_save_hareket', { kalemId: k.id, tutar: k.kalan, tarih, kisiId: kid, kur: kurAl() });
  };

  return (
    <div className={`rounded-lg border ${t.card} ${k.durum === 'odendi' ? 'opacity-70' : ''}`}>
      <div className="flex items-center gap-2 text-xs px-2.5 py-2 cursor-pointer" onClick={topla}>
        <span title={sahip?.ad || 'sahipsiz'} className="text-sm shrink-0 w-5 text-center">
          {sahip?.emoji || '—'}
        </span>
        {gelir ? <ArrowDownLeft size={12} className="text-emerald-400 shrink-0" />
               : <ArrowUpRight size={12} className="text-rose-400 shrink-0" />}
        <span className="font-medium truncate">{k.ad}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${t.sub} ${t.muted} shrink-0 hidden sm:inline`}>
          {TUR_AD[k.tur] || k.tur}
        </span>
        {k.sablonId === 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded border border-violet-500/30 bg-violet-500/10 text-violet-300 shrink-0 hidden md:inline">
            tek seferlik
          </span>
        )}
        <span className="ml-auto tabular-nums shrink-0 text-right">
          {para(k.tutar, k.para)}
          {/* TRY satırının yaklaşık USDC maliyeti: ödenmiş kısım kendi kuruyla,
              kalan güncel kurla. Hiç ödeme yoksa tutarın tamamı tahmin. */}
          {fxli && (k.usdTutar != null || (k.usdOdenen || 0) > 0) && (
            <span className={`block text-[10px] leading-tight ${t.muted}`}
              title={k.odenen > 0
                ? `ödenen ${usdc(k.usdOdenen)} (kendi kuruyla) + kalan ${usdc(k.usdKalan)} (güncel kur)`
                : 'güncel kurla tahmin'}>
              {k.odenen > 0
                ? `${usdc(k.usdOdenen)}${(k.usdKalan || 0) > 0 ? ` + ${usdc(k.usdKalan)}` : ''}`
                : usdc(k.usdTutar)}
              {k.usdKurYok && <AlertTriangle size={9} className="inline ml-1 -mt-0.5 text-amber-400" />}
            </span>
          )}
        </span>
        {/* Durum hücresi iki satır: üstte nerede olduğumuz, altta ne kaldığı.
            ASGARİ ÖDENDİ, "ödendi" ile karıştırılmasın diye ayrı renkte ve
            altında kalan borç duruyor — kartın kapanmadığı görünür kalıyor. */}
        <span className={`tabular-nums shrink-0 w-32 text-right ${renk}`}>
          {k.durum === 'odendi' ? <><Check size={11} className="inline -mt-0.5" /> ödendi</>
            : k.asgariOdendi ? <span className="text-emerald-400">asgari ödendi</span>
            : k.odenen > 0 ? `${para(k.odenen, k.para)} ödendi`
            : <span className={t.muted}>kalan {para(k.kalan, k.para)}</span>}
          {k.durum !== 'odendi' && (k.asgariOdendi || k.asgari > 0 || k.odenen > 0) && (
            <span className={`block text-[10px] leading-tight ${t.muted}`}>
              {k.asgariOdendi || k.asgari === 0
                ? `kalan ${para(k.kalan, k.para)}`
                : `asgariye ${para(k.asgariKalan, k.para)}`}
            </span>
          )}
        </span>
      </div>

      {acik && (
        <div className={`border-t ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'} px-2.5 py-2 space-y-1.5`}>
          {/* Asgari yalnız kartlarda sorulur; zaten girilmişse türü ne olursa
              olsun düzenlenebilir kalsın (ekstre düzeltmesi gelirse diye). */}
          {(k.tur === 'kart' || k.asgari > 0) && (
            <AsgariSatiri k={k} t={t} alan={alan} fxli={fxli} gonder={gonder} />
          )}
          {k.hareketler.length > 0 ? k.hareketler.map((h: Hareket) => (
            <HareketSatiri key={h.id} h={h} k={k} t={t} alan={alan} kisi={kisi}
              gonder={gonder} fxli={fxli} />
          )) : <p className={`text-[11px] ${t.muted} px-2`}>Henüz ödeme yok.</p>}

          <div className="flex items-center gap-1.5 flex-wrap pt-1">
            <select value={kid} onChange={e => setKid(Number(e.target.value))} className={alan} title="kim ödedi">
              {kisiler.map((p: Kisi) => <option key={p.id} value={p.id}>{p.emoji} {p.ad}</option>)}
              {kisiler.length === 0 && <option value={0}>— kişi yok —</option>}
            </select>
            <input type="date" value={tarih} onChange={e => setTarih(e.target.value)} className={alan} />
            <input value={tutar} onChange={e => setTutar(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') ode(); }}
              placeholder={k.kalan > 0 ? `kalan ${k.kalan}` : 'tutar'} inputMode="decimal"
              className={`${alan} w-28 tabular-nums`} />
            {fxli && (
              <span className="flex items-center gap-1">
                <input value={bid} onChange={e => setBid(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') ode(); }}
                  placeholder="bid" inputMode="decimal" title="bu ödemenin USDC/TRY alış kuru"
                  className={`${alan} w-20 tabular-nums`} />
                <span className={`text-[10px] ${t.muted}`}>
                  {(() => {
                    const b = parseFloat((bid || '').replace(',', '.')) || 0;
                    const v = parseFloat(tutar.replace(',', '.')) || k.kalan || 0;
                    const ef = b * (1 - (fx?.kom || 0) / 100);
                    return ef > 0 && v > 0 ? usdc(Math.round((v / ef) * 100) / 100) : 'kur gir';
                  })()}
                </span>
              </span>
            )}
            <button onClick={ode}
              className="px-2.5 py-1.5 rounded-lg text-[11px] border border-emerald-500/45 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20">
              {k.yon === 'gelir' ? 'tahsilat ekle' : 'ödeme ekle'}
            </button>
            {k.kalan > 0 && (
              <button onClick={tamami}
                className={`px-2.5 py-1.5 rounded-lg text-[11px] border ${t.sub} ${t.muted} hover:text-sky-400`}>
                kalanın tamamı
              </button>
            )}
            <button onClick={() => { if (window.confirm(`"${k.ad}" kalemi ve ödemeleri silinsin mi?`)) gonder('budget_delete_kalem', { id: k.id }); }}
              className={`ml-auto px-2 py-1.5 rounded-lg text-[11px] border ${t.sub} ${t.muted} hover:text-rose-400`}>
              <Trash2 size={11} className="inline -mt-0.5" /> kalemi sil
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── asgari ödeme satırı ──────────────────────────────────────────────────────
// Kaydetme save_kalem'in id'li yolundan geçiyor; o yol TÜM alanları yazdığı için
// ad/yön/tür/para/tutar/vade da geri gönderilmek ZORUNDA, yoksa varsayılanlarla
// ezilirler. Tutarı burada değiştirmiyoruz — ekstre tutarı kendi yerinde kalıyor.
function AsgariSatiri({ k, t, alan, fxli, gonder }: any) {
  const [v, setV] = useState('');
  const yaz = (deger: number) => {
    gonder('budget_save_kalem', {
      id: k.id, sablonId: k.sablonId, kisiId: k.kisiId, ad: k.ad, yon: k.yon,
      tur: k.tur, para: k.para, tutar: k.tutar, vade: k.vade, notu: k.notu,
      asgari: deger,
    });
    setV('');
  };
  const kaydet = () => {
    const n = parseFloat((v || '').replace(',', '.'));
    if (!(n > 0)) return;
    yaz(n);
  };
  return (
    <div className={`flex items-center gap-1.5 flex-wrap text-[11px] px-2 py-1 rounded ${t.sub} border`}>
      <span className={t.muted}>asgari ödeme</span>
      {k.asgari > 0 && (
        <>
          <span className="tabular-nums font-medium">{para(k.asgari, k.para)}</span>
          {k.asgariOdendi
            ? <span className="text-emerald-400">ödendi</span>
            : <span className="text-amber-400 tabular-nums">
                {para(k.asgariKalan, k.para)} kaldı
                {fxli && k.usdAsgariKalan != null && (
                  <span className={t.muted}> ({usdc(k.usdAsgariKalan)})</span>
                )}
              </span>}
        </>
      )}
      <input value={v} onChange={e => setV(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') kaydet(); }}
        placeholder={k.asgari > 0 ? 'değiştir' : 'asgari tutarı'} inputMode="decimal"
        className={`${alan} ml-auto w-28 tabular-nums !py-0.5`} />
      {!!v.trim() && (
        <button onClick={kaydet} className="text-sky-300 hover:text-sky-200" title="asgariyi kaydet">
          <Check size={12} />
        </button>
      )}
      {k.asgari > 0 && !v.trim() && (
        <button onClick={() => yaz(0)} className={`${t.muted} hover:text-rose-400`}
          title="asgariyi kaldır">
          <X size={12} />
        </button>
      )}
    </div>
  );
}

// ── tek bir ödeme satırı ─────────────────────────────────────────────────────
// Kur'u olmayan satır (kolon eklenmeden önce girilmiş ödemeler) sessizce
// "kur yok" diye kalmasın: yerinde kur yazılabiliyor. Güncelleme save_hareket'in
// id'li yolundan geçtiği için diğer alanları da geri göndermek ZORUNLU —
// göndermezsek tarih/tutar/kişi boşla ezilir.
function HareketSatiri({ h, k, t, alan, kisi, gonder, fxli }: any) {
  const [bid, setBid] = useState('');
  const hk = kisi(h.kisiId);
  const yaz = () => {
    const b = parseFloat((bid || '').replace(',', '.'));
    if (!(b > 0)) return;
    gonder('budget_save_hareket', {
      id: h.id, kalemId: k.id, tutar: h.tutar, tarih: h.tarih,
      kisiId: h.kisiId, notu: h.notu, kur: b,
    });
    setBid('');
  };
  return (
    <div className={`flex items-center gap-2 text-[11px] px-2 py-1 rounded ${t.sub} border`}>
      <span className="text-sm w-5 text-center" title={hk?.ad || ''}>{hk?.emoji || '—'}</span>
      <span className={`font-mono ${t.muted}`}>{h.tarih || '—'}</span>
      <span className="tabular-nums text-emerald-400">{para(h.tutar, k.para)}</span>
      {fxli && (h.usd != null ? (
        <span className={`tabular-nums ${t.muted}`}
          title={`bid ${h.kurHam} · komisyon düşülmüş efektif ${h.kur}`}>
          {usdc(h.usd)}
        </span>
      ) : (
        <>
          <span className="text-[10px] text-amber-400" title="bu ödeme kur kaydedilmeden girilmiş">
            kur yok
          </span>
          <input value={bid} onChange={e => setBid(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') yaz(); }}
            placeholder="o günün bid'i" inputMode="decimal"
            className={`${alan} w-28 tabular-nums !py-0.5`} />
          {!!bid.trim() && (
            <button onClick={yaz} className="text-sky-300 hover:text-sky-200" title="kuru yaz">
              <Check size={12} />
            </button>
          )}
        </>
      ))}
      {h.notu && <span className={`truncate ${t.muted}`}>{h.notu}</span>}
      <button onClick={() => gonder('budget_delete_hareket', { id: h.id })}
        className={`ml-auto ${t.muted} hover:text-rose-400`} title="ödemeyi sil">
        <Trash2 size={11} />
      </button>
    </div>
  );
}

// ── tek seferlik kalem ───────────────────────────────────────────────────────
function TekSeferlik({ d, t, alan, ay, kapat, gonder }: any) {
  const [f, setF] = useState<any>({
    ad: '', yon: 'gider', tur: 'diger', para: 'EUR', tutar: '', gun: String(new Date().getDate()),
    kisiId: String(d?.kisiler[0]?.id || 0),
  });
  const set = (k: string, v: string) => setF((p: any) => ({ ...p, [k]: v }));
  const ok = f.ad.trim() && parseFloat((f.tutar || '').replace(',', '.')) > 0;
  return (
    <div className={`rounded-xl border ${t.card} p-3 mb-4`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-semibold">Tek seferlik kalem — {ayEtiket(ay)}</span>
        <button onClick={kapat} className={`ml-auto ${t.muted} hover:text-rose-400`}><X size={14} /></button>
      </div>
      <div className="flex items-end gap-1.5 flex-wrap">
        <input value={f.ad} onChange={e => set('ad', e.target.value)} placeholder="ad (ör. uçak bileti)"
          className={`${alan} flex-1 min-w-[10rem]`} />
        <select value={f.yon} onChange={e => set('yon', e.target.value)} className={alan}>
          <option value="gider">gider</option><option value="gelir">gelir</option>
        </select>
        <select value={f.tur} onChange={e => set('tur', e.target.value)} className={alan}>
          {TURLER.map(x => <option key={x} value={x}>{TUR_AD[x]}</option>)}
        </select>
        <select value={f.kisiId} onChange={e => set('kisiId', e.target.value)} className={alan}>
          {d?.kisiler.map((p: Kisi) => <option key={p.id} value={p.id}>{p.emoji} {p.ad}</option>)}
          {!d?.kisiler.length && <option value="0">— kişi yok —</option>}
        </select>
        <input value={f.tutar} onChange={e => set('tutar', e.target.value)} placeholder="tutar"
          inputMode="decimal" className={`${alan} w-24 tabular-nums`} />
        <select value={f.para} onChange={e => set('para', e.target.value)} className={alan}>
          {PARALAR.map(x => <option key={x} value={x}>{x}</option>)}
        </select>
        <input value={f.gun} onChange={e => set('gun', e.target.value)} placeholder="gün"
          inputMode="numeric" className={`${alan} w-16 tabular-nums`} title="ayın kaçı" />
        <button disabled={!ok}
          onClick={() => { gonder('budget_save_kalem', { ...f, tutar: parseFloat(f.tutar.replace(',', '.')), gun: parseInt(f.gun, 10) || 1, kisiId: parseInt(f.kisiId, 10) || 0 }); kapat(); }}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-600/25 border border-sky-500/50 text-sky-300 hover:bg-sky-600/40 disabled:opacity-40">
          Ekle
        </button>
      </div>
    </div>
  );
}

// ── kişiler + şablonlar ──────────────────────────────────────────────────────
function Ayarlar({ d, t, alan, gonder }: any) {
  const bos = {
    id: 0, ad: '', yon: 'gider', tur: 'kira', para: 'EUR', gun: '1', tutar: '',
    otomatik: true, basAy: '', sonAy: '', kisiId: String(d?.kisiler[0]?.id || 0),
  };
  const [s, setS] = useState<any>(bos);
  const [kAd, setKAd] = useState(''); const [kEmoji, setKEmoji] = useState('🙂');
  const set = (k: string, v: any) => setS((p: any) => ({ ...p, [k]: v }));
  const sOk = s.ad.trim() && (!s.otomatik || parseFloat((s.tutar || '').replace(',', '.')) > 0);

  return (
    <div className={`rounded-xl border ${t.card} p-3 mb-4 space-y-4`}>
      {/* kişiler */}
      <div>
        <div className={`text-[11px] ${t.muted} mb-1.5`}>Kişiler — emoji ödeme satırlarında görünür</div>
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          {d?.kisiler.map((p: Kisi) => (
            <span key={p.id} className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg border ${t.sub}`}>
              <span className="text-sm">{p.emoji}</span>{p.ad}
              <button onClick={() => { if (window.confirm(`${p.ad} silinsin mi? Kayıtlar kalır, sahipsiz olur.`)) gonder('budget_delete_kisi', { id: p.id }); }}
                className={`${t.muted} hover:text-rose-400 ml-0.5`}><X size={11} /></button>
            </span>
          ))}
          {!d?.kisiler.length && <span className={`text-xs ${t.muted}`}>Henüz kişi yok.</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <input value={kEmoji} onChange={e => setKEmoji(e.target.value)} className={`${alan} w-14 text-center text-base`} />
          <input value={kAd} onChange={e => setKAd(e.target.value)} placeholder="kişi adı"
            onKeyDown={e => { if (e.key === 'Enter' && kAd.trim()) { gonder('budget_save_kisi', { ad: kAd, emoji: kEmoji }); setKAd(''); } }}
            className={`${alan} w-40`} />
          <button disabled={!kAd.trim()}
            onClick={() => { gonder('budget_save_kisi', { ad: kAd, emoji: kEmoji }); setKAd(''); }}
            className="px-2.5 py-1.5 rounded-lg text-[11px] border border-sky-500/45 bg-sky-500/10 text-sky-300 disabled:opacity-40">
            <Plus size={11} className="inline -mt-0.5" /> kişi
          </button>
        </div>
      </div>

      {/* şablonlar */}
      <div className={`pt-3 border-t ${t.sub}`}>
        <div className={`text-[11px] ${t.muted} mb-1.5`}>
          Şablonlar — <b>otomatik</b> olanlar (kira, kredi, maaş) her ay kendiliğinden düşer;
          kredi kartında tutarı her ay sen yazarsın. Kredide <b>son taksit ayı</b> yazarsan o aydan sonra gelmez.
        </div>
        <div className="space-y-1 mb-2">
          {d?.sablonlar.map((x: Sablon) => {
            const sahip = d.kisiler.find((p: Kisi) => p.id === x.kisi_id);
            return (
              <div key={x.id} className={`flex items-center gap-2 text-[11px] px-2 py-1.5 rounded-lg border ${t.sub} ${x.aktif ? '' : 'opacity-50'}`}>
                <span className="text-sm w-5 text-center">{sahip?.emoji || '—'}</span>
                <span className={`tabular-nums w-6 ${t.muted}`}>{String(x.gun).padStart(2, '0')}</span>
                <span className="font-medium truncate">{x.ad}</span>
                <span className={`px-1.5 rounded border ${t.sub} ${t.muted}`}>{TUR_AD[x.tur] || x.tur}</span>
                <span className={x.yon === 'gelir' ? 'text-emerald-400' : 'text-rose-400'}>{x.yon}</span>
                <span className="tabular-nums ml-auto">
                  {x.otomatik ? para(x.tutar, x.para) : <span className={t.muted}>elle · {x.para}</span>}
                </span>
                {x.son_ay && <span className="text-amber-400 shrink-0">son {x.son_ay}</span>}
                <button onClick={() => setS({
                    id: x.id, ad: x.ad, yon: x.yon, tur: x.tur, para: x.para, gun: String(x.gun),
                    tutar: x.tutar ? String(x.tutar) : '', otomatik: !!x.otomatik,
                    basAy: x.bas_ay || '', sonAy: x.son_ay || '', kisiId: String(x.kisi_id),
                  })}
                  className={`${t.muted} hover:text-sky-400 shrink-0`}><Pencil size={11} /></button>
                <button onClick={() => { if (window.confirm(`"${x.ad}" şablonu silinsin mi? Geçmiş aylardaki kalemler KALIR.`)) gonder('budget_delete_sablon', { id: x.id }); }}
                  className={`${t.muted} hover:text-rose-400 shrink-0`}><Trash2 size={11} /></button>
              </div>
            );
          })}
          {!d?.sablonlar.length && <p className={`text-xs ${t.muted}`}>Henüz şablon yok.</p>}
        </div>

        <div className="flex items-end gap-1.5 flex-wrap">
          <input value={s.ad} onChange={e => set('ad', e.target.value)} placeholder="ad (Kira / Konut kredisi / Maaş)"
            className={`${alan} flex-1 min-w-[11rem]`} />
          <select value={s.kisiId} onChange={e => set('kisiId', e.target.value)} className={alan}>
            {d?.kisiler.map((p: Kisi) => <option key={p.id} value={p.id}>{p.emoji} {p.ad}</option>)}
            {!d?.kisiler.length && <option value="0">— kişi yok —</option>}
          </select>
          <select value={s.yon} onChange={e => set('yon', e.target.value)} className={alan}>
            <option value="gider">gider</option><option value="gelir">gelir</option>
          </select>
          <select value={s.tur} onChange={e => {
              const v = e.target.value;
              // Kart = tutarı her ay elle girilen tür; seçilince otomatik kapanır.
              setS((p: any) => ({ ...p, tur: v, otomatik: v === 'kart' ? false : p.otomatik }));
            }} className={alan}>
            {TURLER.map(x => <option key={x} value={x}>{TUR_AD[x]}</option>)}
          </select>
          <input value={s.gun} onChange={e => set('gun', e.target.value)} placeholder="gün"
            inputMode="numeric" className={`${alan} w-16 tabular-nums`} title="ayın kaçı" />
          <select value={s.para} onChange={e => set('para', e.target.value)} className={alan}>
            {PARALAR.map(x => <option key={x} value={x}>{x}</option>)}
          </select>
          <label className={`flex items-center gap-1 text-[11px] px-2 py-1.5 rounded-lg border ${t.sub} cursor-pointer`}>
            <input type="checkbox" className="accent-sky-500" checked={s.otomatik}
              onChange={e => set('otomatik', e.target.checked)} />
            otomatik düşsün
          </label>
          {s.otomatik && (
            <input value={s.tutar} onChange={e => set('tutar', e.target.value)} placeholder="tutar"
              inputMode="decimal" className={`${alan} w-24 tabular-nums`} />
          )}
          <input value={s.sonAy} onChange={e => set('sonAy', e.target.value)} placeholder="son taksit (2027-04)"
            className={`${alan} w-36`} title="kredide son taksit ayı — bu aydan sonra düşmez" />
          <button disabled={!sOk}
            onClick={() => {
              gonder('budget_save_sablon', {
                ...s, gun: parseInt(s.gun, 10) || 1, kisiId: parseInt(s.kisiId, 10) || 0,
                tutar: s.otomatik ? parseFloat((s.tutar || '0').replace(',', '.')) : 0,
              });
              setS(bos);
            }}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-600/25 border border-sky-500/50 text-sky-300 hover:bg-sky-600/40 disabled:opacity-40">
            {s.id ? 'Güncelle' : 'Şablon ekle'}
          </button>
          {!!s.id && (
            <button onClick={() => setS(bos)} className={`px-2.5 py-1.5 rounded-lg text-[11px] border ${t.sub} ${t.muted}`}>
              vazgeç
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
